import React from "react";
import { useCart } from "@/context/CartContext";
import { Link } from "react-router-dom";
import { X, Plus, Minus } from "lucide-react";
import { formatPrice } from "@/lib/currency";

export default function CartDrawer() {
  const { items, open, setOpen, update, remove, subtotal } = useCart();

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 bg-black/40 z-[70] transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-[#F5F5F0] z-[80] shadow-2xl transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5DF]">
          <div className="serif text-2xl">Your Selection</div>
          <button data-testid="cart-close-button" onClick={() => setOpen(false)}><X size={22} /></button>
        </div>

        <div className="flex flex-col h-[calc(100%-180px)] overflow-y-auto p-6 gap-6">
          {items.length === 0 && (
            <div className="text-center mt-20">
              <div className="overline">Your bag is empty</div>
              <p className="serif text-xl mt-4">A curated edit awaits.</p>
              <Link onClick={() => setOpen(false)} to="/shop" className="btn-outline mt-8" data-testid="cart-empty-shop-link">Discover the shop</Link>
            </div>
          )}
          {items.map((it) => (
            <div key={it.key} className="flex gap-4 pb-6 border-b border-[#E5E5DF]" data-testid={`cart-item-${it.product_id}`}>
              <img src={it.image} alt={it.name} className="w-24 h-32 object-cover" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between">
                  <Link to={`/product/${it.product_id}`} onClick={() => setOpen(false)} className="serif text-lg leading-tight">{it.name}</Link>
                  <button onClick={() => remove(it.key)} aria-label="Remove" data-testid={`cart-remove-${it.product_id}`}><X size={14} /></button>
                </div>
                <div className="text-xs text-[#5A5A5A] mt-1">
                  {it.size && <>Size: {it.size}</>} {it.color && <> • {it.color}</>}
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="inline-flex items-center border border-[#E5E5DF]">
                    <button onClick={() => update(it.key, it.quantity - 1)} className="px-2 py-1"><Minus size={12} /></button>
                    <span className="px-3 text-sm">{it.quantity}</span>
                    <button onClick={() => update(it.key, it.quantity + 1)} className="px-2 py-1"><Plus size={12} /></button>
                  </div>
                  <div className="text-sm whitespace-nowrap">{formatPrice(it.price * it.quantity)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 bg-[#F5F5F0] border-t border-[#E5E5DF]">
          <div className="flex justify-between mb-4">
            <span className="overline">Subtotal</span>
            <span className="serif text-2xl" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
          </div>
          <Link
            to="/checkout"
            onClick={() => setOpen(false)}
            data-testid="cart-checkout-button"
            className={`btn-primary w-full justify-center ${items.length === 0 ? "opacity-40 pointer-events-none" : ""}`}
          >
            Proceed to checkout
          </Link>
        </div>
      </aside>
    </>
  );
}
